import { type FC } from 'react';
import { FaCalendarAlt, FaUser, FaClock, FaBus, FaFileAlt, FaLayerGroup } from 'react-icons/fa';
import { useProfile } from '@Hooks/useProfile';
import Spinner from '@Components/common/spinner/Spinner';
import '@Styles/profile.css';

const ProfilePage: FC = () => {
  const { data, loading, error } = useProfile();

  if (loading) {
    return <Spinner showSpinner />;
  }

  if (error) {
    return (
      <div className="profile__page">
        <p className="profile__error">Error al cargar el perfil: {error}</p>
      </div>
    );
  }

  return (
    <div className="profile__page">
      <h1 className="profile__page-title">Información general</h1>

      <section className="profile__section">
        <h2 className="profile__section-title">Información básica</h2>

        <div className="profile__basic-info">
          <div className="profile__identity">
            <div className="profile__avatar">
              {data?.basicInformation.photo ? (
                <img
                  src={
                    data.basicInformation.photo?.startsWith('http') ||
                    data.basicInformation.photo?.startsWith('data:')
                      ? data.basicInformation.photo
                      : `data:image/jpeg;base64,${data.basicInformation.photo}`
                  }
                  alt={`${data.basicInformation.name} ${data.basicInformation.lastNames.full}`}
                  className="profile__avatar-img"
                />
              ) : (
                <span>
                  {data?.basicInformation
                    ? `${data.basicInformation.name[0]}${data.basicInformation.lastNames.first[0]}`.toUpperCase()
                    : '--'}
                </span>
              )}
            </div>
            <div className="profile__full-name">
              <p className="profile__full-name-label">Nombre completo</p>
              <p className="profile__full-name-value">
                {data
                  ? `${data.basicInformation.name} ${data.basicInformation.lastNames.full}`
                  : '-'}
              </p>
            </div>
          </div>

          <div className="profile__fields">
            <div>
              <p className="profile__field-label">Nº de tarjeta</p>
              <p className="profile__field-value">{data?.payroll ?? '-'}</p>
            </div>
            <div>
              <p className="profile__field-label">Teléfono</p>
              <p className="profile__field-value">{data?.basicInformation.phoneNumber ?? '-'}</p>
            </div>
            <div>
              <p className="profile__field-label">Email</p>
              <p className="profile__field-value">{data?.basicInformation.email ?? '-'}</p>
            </div>
            <div>
              <p className="profile__field-label">Estado</p>
              <p className="profile__field-value">{data?.basicInformation.status ?? '-'}</p>
            </div>
            <div className="profile__field--full">
              <p className="profile__field-label">Residencia</p>
              <p className="profile__field-value">{data?.basicInformation.residence ?? '-'}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="profile__section">
        <h2 className="profile__section-title">Información profesional</h2>

        <div className="profile__pro-grid">
          <div className="profile__pro-item">
            <FaCalendarAlt className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Plaza actual</p>
              <p className="profile__pro-value">{data?.professionalInfo.currentFleet ?? '-'}</p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaUser className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Tipo de contrato</p>
              <p className="profile__pro-value">
                {data?.professionalInfo.contractType.desc ?? '-'}
              </p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaCalendarAlt className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Plaza anterior</p>
              <p className="profile__pro-value">{data?.professionalInfo.lastFleet ?? '-'}</p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaClock className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Antigüedad</p>
              <p className="profile__pro-value">{data?.professionalInfo.seniority ?? '-'}</p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaUser className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Supervisor</p>
              <p className="profile__pro-value">{data?.professionalInfo.supervisor ?? '-'}</p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaBus className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Servicio de recogida</p>
              <p className="profile__pro-value">
                {data ? (data.professionalInfo.pickupService ? 'Sí' : 'No') : '-'}
              </p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaFileAlt className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Puesto</p>
              <p className="profile__pro-value">{data?.professionalInfo.jobRole.desc ?? '-'}</p>
            </div>
          </div>

          <div className="profile__pro-item">
            <FaLayerGroup className="profile__pro-icon" />
            <div className="profile__pro-content">
              <p className="profile__pro-label">Categoría</p>
              <p className="profile__pro-value">{data?.professionalInfo.category ?? '-'}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;
